import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import "bootstrap-icons/font/bootstrap-icons.css";

const APPEAR_DURATION = 0.5;

const backdropVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { duration: APPEAR_DURATION, ease: "easeOut" },
    },
};

const panelVariants = {
    hidden: { opacity: 0, y: 28, scale: 0.96 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: APPEAR_DURATION, ease: [0.22, 1, 0.36, 1] },
    },
};

export default function BlurredPrompt({
    open,
    title,
    description,
    confirmLabel = "Continue",
    cancelLabel = "Cancel",
    onConfirm,
    onCancel,
    confirmBusy = false,
    titleId = "blurred-prompt-title",
    iconClass = "bi-exclamation-triangle-fill",
    iconWrapperClassName = "bg-amber-100 text-amber-700",
    confirmButtonClassName = "btn btn-dark-app",
}) {
    useEffect(() => {
        if (!open) return undefined;

        const previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleKeyDown = (event) => {
            if (event.key === "Escape" && !confirmBusy) onCancel();
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onCancel, confirmBusy]);

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
        >
            <motion.div
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                aria-hidden
                initial="hidden"
                animate="visible"
                variants={backdropVariants}
                onClick={confirmBusy ? undefined : onCancel}
            />

            <motion.div
                className="relative z-10 w-full max-w-md rounded-2xl border border-white/20 bg-white/95 p-6 shadow-2xl backdrop-blur-md md:p-8"
                initial="hidden"
                animate="visible"
                variants={panelVariants}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="mb-6 flex gap-3">
                    <span
                        className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${iconWrapperClassName}`}
                    >
                        <i className={`bi ${iconClass} text-lg`} aria-hidden />
                    </span>
                    <div className="min-w-0">
                        <h2 id={titleId} className="mb-1 text-xl font-semibold text-slate-900">
                            {title}
                        </h2>
                        {description ? (
                            <p className="mb-0 text-sm leading-relaxed text-slate-600">{description}</p>
                        ) : null}
                    </div>
                </div>

                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        className="btn btn-secondary-app px-4 py-2.5 text-sm font-semibold"
                        onClick={onCancel}
                        disabled={confirmBusy}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        className={`${confirmButtonClassName} px-4 py-2.5 text-sm font-semibold`}
                        onClick={onConfirm}
                        disabled={confirmBusy}
                    >
                        {confirmBusy ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden
                                />
                                Please wait…
                            </>
                        ) : (
                            confirmLabel
                        )}
                    </button>
                </div>
            </motion.div>
        </div>,
        document.body
    );
}
